import React from "react";
import { Link } from "react-router-dom";
import {
  Moon, ArrowRight, Home, Car, Users, HeartPulse, ClipboardList, Dumbbell, Check,
} from "lucide-react";
import StarField from "../components/StarField";
import { SERVICES } from "../mock/mockData";

const ICONS = { Home, Car, Users, HeartPulse, ClipboardList, Dumbbell };

const ServicesPage = () => {
  return (
    <div className="relative" data-testid="services-page">
      {/* Header */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <StarField count={50} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,165,116,0.15),transparent_60%)]" />
        <div className="container-lux relative z-10 max-w-4xl fade-up">
          <div className="divider-gold mb-6">Our Services</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mb-8">
            Six ways we <em className="text-gold not-italic">go further</em> for you.
          </h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
            Whether you need a hand around the home or a full-time partner in community life, we build a support plan that fits <em className="text-gold not-italic">exactly</em> what you need.
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="py-20 md:py-28">
        <div className="container-lux space-y-24 md:space-y-32">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const reversed = i % 2 === 1;
            return (
              <div
                key={s.id}
                id={s.id}
                className={`grid lg:grid-cols-12 gap-12 items-center ${reversed ? "lg:flex-row-reverse" : ""}`}
                data-testid={`service-detail-${s.id}`}
              >
                {/* Image */}
                <div className={`lg:col-span-6 ${reversed ? "lg:order-2" : ""}`}>
                  <div className="relative rounded-[2rem] overflow-hidden aspect-[4/3] lux-card">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-deep/60 via-transparent to-transparent" />
                    <div className="absolute top-6 left-6 w-14 h-14 rounded-full bg-deep/80 backdrop-blur-md border border-gold/30 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-gold" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={`lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
                  <div className="text-xs tracking-[0.25em] uppercase text-gold mb-4">Service {String(i + 1).padStart(2, "0")}</div>
                  <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-4">{s.title}</h2>
                  <p className="text-white/60 italic font-serif text-lg mb-8">{s.tagline}</p>

                  <ul className="space-y-4 mb-10">
                    {s.items.map((item) => (
                      <li key={item.name} className="flex gap-4" data-testid={`service-item-${s.id}-${item.name.replace(/\s+/g, "-").toLowerCase()}`}>
                        <div className="w-6 h-6 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-gold" />
                        </div>
                        <div>
                          <div className="text-white font-medium">{item.name}</div>
                          <div className="text-sm text-white/55 mt-0.5">{item.desc}</div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/book"
                    className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-xs font-semibold tracking-[0.15em] uppercase text-gold hover:bg-gold/10 transition-all link-gold"
                    data-testid={`book-${s.id}`}
                  >
                    Book This Service <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 md:py-36" data-testid="services-cta">
        <div className="container-lux">
          <div className="lux-card rounded-[2rem] p-12 md:p-20 text-center max-w-4xl mx-auto relative overflow-hidden">
            <div className="absolute -bottom-20 -left-20 w-80 h-80 pointer-events-none opacity-40">
              <div className="gold-arc w-full h-full" />
            </div>
            <Moon className="w-12 h-12 text-gold mx-auto mb-6 moon-glow" />
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-6">
              Not sure which service is <em className="text-gold not-italic">right for you?</em>
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
              Book a free 15-minute consultation and we'll help you build a plan that matches your life, your goals, and your NDIS funding.
            </p>
            <Link to="/book" className="btn-gold inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-[0.12em] uppercase text-deep hover:bg-gold-light transition-all hover:scale-[1.03]" data-testid="services-cta-book">
              <Moon className="w-4 h-4" /> Free Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
